import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d && d.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function pickCustomerFromResponse(responseData, customerId) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (dataBlock == null) return null
  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') return null

  if (dataBlock.customer && typeof dataBlock.customer === 'object') {
    return dataBlock.customer
  }

  if (
    !Array.isArray(dataBlock) &&
    dataBlock.customers == null &&
    (dataBlock._id != null || dataBlock.customerName != null)
  ) {
    return dataBlock
  }

  const list = dataBlock.customers
  if (!Array.isArray(list) || list.length === 0) return null
  const found = list.find((c) => String(c._id || c.id) === String(customerId))
  return found || list[0]
}

const emptyContact = () => ({
  _id: '',
  contactPerson: '',
  designation: '',
  contactNumber: '',
  email: '',
  address: '',
  city: '',
  state: ''
})

function normalizeGstDetails(responseData) {
  const data = responseData?.data
  if (!data || typeof data !== 'object') return null

  return {
    gstin: data.gstin || '',
    legalName: data.legalName || '',
    tradeName: data.tradeName || '',
    state: data.state || '',
    status: data.status || '',
    taxpayerType: data.taxPayerType || '',
    constitutionType: data.constitutionType || '',
    registrationDate: data.registrationDate || ''
  }
}

function isGstActiveStatus(status) {
  return String(status || '').trim().toLowerCase() === 'active'
}

function mapContactFromApi(c) {
  if (!c || typeof c !== 'object') return emptyContact()
  return {
    _id: c._id != null ? String(c._id) : '',
    contactPerson: c.contactPerson || '',
    designation: c.designation || '',
    contactNumber: (c.contactNumber || '').replace(/\D/g, '').slice(0, 10),
    email: c.email || '',
    address: c.address || '',
    city: c.city || '',
    state: c.state || ''
  }
}

const CustomerMasterEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [customerName, setCustomerName] = useState('')
  const [gstNumber, setGstNumber] = useState('')
  const [contacts, setContacts] = useState([emptyContact()])
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(true)
  const [isVerifyingGst, setIsVerifyingGst] = useState(false)
  const [isFetchingGst, setIsFetchingGst] = useState(false)
  const [gstDetails, setGstDetails] = useState(null)
  const [gstError, setGstError] = useState('')
  const [gstCaptchaImage, setGstCaptchaImage] = useState('')
  const [gstSessionCookies, setGstSessionCookies] = useState('')
  const [gstCaptchaInput, setGstCaptchaInput] = useState('')
  const [showGstCaptcha, setShowGstCaptcha] = useState(false)

  const addContact = () => {
    setContacts((prev) => [...prev, emptyContact()])
  }

  const removeContact = (index) => {
    if (contacts.length <= 1) return
    setContacts((prev) => prev.filter((_, i) => i !== index))
  }

  const updateContact = (index, field, value) => {
    setContacts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    )
  }

  const handleContactPhoneChange = (index, e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10)
    updateContact(index, 'contactNumber', value)
  }

  const handleContactCityChange = (index, e) => {
    const value = e.target.value.replace(new RegExp('[^a-zA-Z\\s,/-]', 'g'), '')
    updateContact(index, 'city', value)
  }

  const handleContactStateChange = (index, e) => {
    const value = e.target.value.replace(/[^a-zA-Z\s]/g, '')
    updateContact(index, 'state', value)
  }

  const handleGstChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (value.length <= 15) {
      setGstNumber(value)
      setGstDetails(null)
      setGstError('')
      setGstCaptchaImage('')
      setGstSessionCookies('')
      setGstCaptchaInput('')
      setShowGstCaptcha(false)
    }
  }

  const fetchGstCaptcha = async () => {
    const response = await axios.post(API_URL, { action: 'get-gst-captcha' })
    const responseData = parseApiResponse(response.data)

    if (responseData?.success !== true || !responseData?.data?.captchaImage) {
      throw new Error(responseData?.message || 'Failed to load GST captcha')
    }

    setGstCaptchaImage(responseData.data.captchaImage)
    setGstSessionCookies(responseData.data.sessionCookies)
    setGstCaptchaInput('')
    setShowGstCaptcha(true)
    return responseData.data
  }

  const submitGstVerification = async (gstValue, captcha, sessionCookies) => {
    const response = await axios.post(API_URL, {
      action: 'verify-gst',
      gstNumber: gstValue,
      captcha: captcha.trim(),
      sessionCookies
    })
    return parseApiResponse(response.data)
  }

  const handleFetchGstCaptcha = async () => {
    const gstValue = gstNumber.trim().toUpperCase()

    if (!gstValue) {
      toast.error('Please enter GST number', { autoClose: 2000 })
      return
    }

    setIsFetchingGst(true)
    setGstError('')
    setGstDetails(null)

    try {
      await fetchGstCaptcha()
      toast.info('Enter the captcha below and click Verify', { autoClose: 3000 })
    } catch (error) {
      console.error('GST captcha fetch error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      const message =
        errData?.message || error.message || 'Unable to load GST captcha. Please try again.'
      setGstError(message)
      toast.error(message, { autoClose: 2000 })
    } finally {
      setIsFetchingGst(false)
    }
  }

  const handleVerifyGst = async () => {
    const gstValue = gstNumber.trim().toUpperCase()

    if (!gstValue) {
      toast.error('Please enter GST number', { autoClose: 2000 })
      return
    }

    if (!showGstCaptcha || !gstSessionCookies) {
      toast.error('Please fetch captcha first', { autoClose: 2000 })
      return
    }

    if (!gstCaptchaInput.trim()) {
      toast.error('Please enter the captcha', { autoClose: 2000 })
      return
    }

    setIsVerifyingGst(true)
    setGstError('')

    try {
      const responseData = await submitGstVerification(
        gstValue,
        gstCaptchaInput,
        gstSessionCookies
      )

      if (responseData?.success === true) {
        const details = normalizeGstDetails(responseData)
        if (!details) {
          setGstError('GST details not found in response. Please try again.')
          toast.error('GST verification failed', { autoClose: 2000 })
          return
        }

        setGstDetails(details)
        setShowGstCaptcha(false)

        if (!isGstActiveStatus(details.status)) {
          setGstError(`GST number is not active. Current status: ${details.status || 'Unknown'}`)
          toast.error('GST number is not active', { autoClose: 2000 })
          return
        }

        toast.success('GST number verified successfully', { autoClose: 2000 })
      } else {
        const message = responseData?.message || 'Invalid GST number. Please check and try again.'
        setGstError(message)
        toast.error(message, { autoClose: 2000 })
        await fetchGstCaptcha()
      }
    } catch (error) {
      console.error('GST verification error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      const message =
        errData?.message || error.message || 'Unable to verify GST number. Please try again.'
      setGstError(message)
      toast.error(message, { autoClose: 2000 })
      try {
        await fetchGstCaptcha()
      } catch {
        // ignore refresh failure
      }
    } finally {
      setIsVerifyingGst(false)
    }
  }

  const fetchCustomer = async () => {
    if (!id) {
      toast.error('Customer ID is missing', { autoClose: 2000 })
      navigate('/customerMaster')
      return
    }

    setIsFetching(true)
    try {
      const payload = { action: 'get-customer-by-id', id }
      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (!isApiSuccess(responseData)) {
        toast.error('Failed to fetch customer details', { autoClose: 2000 })
        navigate('/customerMaster')
        return
      }

      const row = pickCustomerFromResponse(responseData, id)
      if (!row) {
        toast.error('Customer not found', { autoClose: 2000 })
        navigate('/customerMaster')
        return
      }

      setCustomerName(row.customerName || '')
      setGstNumber(String(row.gstNumber || '').toUpperCase())
      setGstDetails(null)
      setGstError('')
      setShowGstCaptcha(false)
      const list = Array.isArray(row.contacts) ? row.contacts.map(mapContactFromApi) : []
      setContacts(list.length > 0 ? list : [emptyContact()])
    } catch (error) {
      console.error('Fetch customer error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      const msg =
        typeof errData === 'object' && errData?.message
          ? errData.message
          : 'An error occurred while fetching customer details'
      toast.error(msg, { autoClose: 2000 })
      navigate('/customerMaster')
    } finally {
      setIsFetching(false)
    }
  }

  useEffect(() => {
    fetchCustomer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const validateForm = () => {
    if (!customerName.trim()) {
      toast.error('Customer name is required', { autoClose: 2000 })
      return false
    }
    for (let i = 0; i < contacts.length; i++) {
      const c = contacts[i]
      const label = `Contact ${i + 1}`
      if (!c.contactPerson.trim()) {
        toast.error(`${label}: Contact person is required`, { autoClose: 2500 })
        return false
      }
      if (!c.email.trim()) {
        toast.error(`${label}: Email is required`, { autoClose: 2500 })
        return false
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email.trim())) {
        toast.error(`${label}: Please enter a valid email`, { autoClose: 2500 })
        return false
      }
      if (!c.contactNumber.trim()) {
        toast.error(`${label}: Contact number is required`, { autoClose: 2500 })
        return false
      }
      if (c.contactNumber.length !== 10) {
        toast.error(`${label}: Contact number must be 10 digits`, { autoClose: 2500 })
        return false
      }
      if (!c.address.trim()) {
        toast.error(`${label}: Address is required`, { autoClose: 2500 })
        return false
      }
      if (!c.city.trim()) {
        toast.error(`${label}: City is required`, { autoClose: 2500 })
        return false
      }
      if (!c.state.trim()) {
        toast.error(`${label}: State is required`, { autoClose: 2500 })
        return false
      }
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      setTimeout(() => navigate('/'), 2000)
      return
    }

    setIsLoading(true)
    try {
      const contactsPayload = contacts.map((c) => {
        const row = {
          contactPerson: c.contactPerson.trim(),
          designation: c.designation.trim(),
          contactNumber: c.contactNumber.trim(),
          email: c.email.trim(),
          address: c.address.trim(),
          city: c.city.trim(),
          state: c.state.trim(),
          status: 'active'
        }
        if (c._id && String(c._id).trim()) {
          row._id = String(c._id).trim()
        }
        return row
      })

      const payload = {
        action: 'update-customer',
        id,
        requestUserId: userId,
        customerName: customerName.trim(),
        contacts: contactsPayload
      }

      if (gstNumber.trim()) {
        payload.gstNumber = gstNumber.trim().toUpperCase()
        if (gstDetails) {
          payload.gstStatus = gstDetails.status || ''
          payload.gstLegalName = gstDetails.legalName || ''
          payload.gstTradeName = gstDetails.tradeName || ''
          payload.gstConstitutionType = gstDetails.constitutionType || ''
          payload.taxPayerType = gstDetails.taxpayerType || ''
          payload.gstRegistrationDate = gstDetails.registrationDate || ''
          payload.gstState = gstDetails.state || ''
        }
      } else {
        payload.gstNumber = ''
      }

      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData.message || 'Customer updated successfully', {
          autoClose: 2000
        })
        navigate('/customerMaster', { replace: true })
      } else {
        toast.error(responseData?.message || 'Failed to update customer', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Update customer error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      const msg =
        typeof errData === 'object' && errData?.message
          ? errData.message
          : error?.message || 'An error occurred while updating customer'
      toast.error(msg, { autoClose: 2000 })
    } finally {
      setIsLoading(false)
    }
  }

  if (isFetching) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">Edit Customer</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/customerMaster">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Edit Customer</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/customerMaster">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Customer Name</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Customer / company name"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">GST Number</label>
                        <div className="col-12 col-md-8">
                          <div className="input-group">
                            <input
                              type="text"
                              name="gstNumber"
                              className="form-control"
                              placeholder="GST Number (e.g., 29AAKCS7108C1ZV)"
                              value={gstNumber}
                              onChange={handleGstChange}
                              maxLength="15"
                            />
                            <div className="input-group-append">
                              <button
                                type="button"
                                className="btn2 btn-info"
                                onClick={handleFetchGstCaptcha}
                                disabled={isFetchingGst || !gstNumber}
                              >
                                {isFetchingGst ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin mr-1"></i>
                                    Fetching...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa fa-download mr-1"></i>
                                    Fetch
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                          <small className="form-text text-muted">
                            Optional: Enter GST number, fetch captcha, then verify registration details
                          </small>

                          {showGstCaptcha && gstCaptchaImage && (
                            <div
                              className="mt-3 p-3 border rounded"
                              style={{ backgroundColor: '#f8f9fa' }}
                            >
                              <p className="mb-2" style={{ fontSize: '13px', fontWeight: 600 }}>
                                Enter the captcha below
                              </p>
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="d-flex align-items-center flex-wrap">
                                  <img
                                    src={gstCaptchaImage}
                                    alt="GST Captcha"
                                    style={{
                                      height: '48px',
                                      border: '1px solid #ccc',
                                      borderRadius: '4px'
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm ml-2"
                                    onClick={async () => {
                                      try {
                                        setIsFetchingGst(true)
                                        await fetchGstCaptcha()
                                        toast.success('Captcha refreshed', { autoClose: 1500 })
                                      } catch {
                                        toast.error('Failed to refresh captcha', { autoClose: 2000 })
                                      } finally {
                                        setIsFetchingGst(false)
                                      }
                                    }}
                                    disabled={isFetchingGst}
                                  >
                                    Refresh captcha
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  className="btn2 btn-info ml-2"
                                  onClick={handleVerifyGst}
                                  disabled={isVerifyingGst || !gstCaptchaInput.trim()}
                                >
                                  {isVerifyingGst ? (
                                    <>
                                      <i className="fa fa-spinner fa-spin mr-1"></i>
                                      Verifying...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fa fa-check-circle mr-1"></i>
                                      Verify
                                    </>
                                  )}
                                </button>
                              </div>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter captcha"
                                value={gstCaptchaInput}
                                onChange={(e) => setGstCaptchaInput(e.target.value)}
                                maxLength={10}
                                autoComplete="off"
                              />
                              <small className="form-text text-muted">
                                Enter the captcha and click Verify
                              </small>
                            </div>
                          )}

                          {gstDetails && (
                            <div
                              className="mt-2 p-3"
                              style={{
                                backgroundColor: isGstActiveStatus(gstDetails.status)
                                  ? '#d4edda'
                                  : '#fff3cd',
                                border: isGstActiveStatus(gstDetails.status)
                                  ? '1px solid #c3e6cb'
                                  : '1px solid #ffeeba',
                                borderRadius: '4px'
                              }}
                            >
                              <div className="d-flex align-items-start">
                                <i
                                  className={`fa ${
                                    isGstActiveStatus(gstDetails.status)
                                      ? 'fa-check-circle text-success'
                                      : 'fa-exclamation-triangle text-warning'
                                  } mr-2`}
                                  style={{ fontSize: '18px', marginTop: '2px' }}
                                ></i>
                                <div style={{ flex: 1 }}>
                                  <p
                                    className="mb-1"
                                    style={{
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      color: isGstActiveStatus(gstDetails.status)
                                        ? '#155724'
                                        : '#856404'
                                    }}
                                  >
                                    {isGstActiveStatus(gstDetails.status)
                                      ? 'GST Verified Successfully'
                                      : 'GST Found But Not Active'}
                                  </p>
                                  <div
                                    style={{
                                      fontSize: '12px',
                                      color: isGstActiveStatus(gstDetails.status)
                                        ? '#155724'
                                        : '#856404'
                                    }}
                                  >
                                    <p className="mb-1">
                                      <strong>Status:</strong>{' '}
                                      <span
                                        className={`badge ${
                                          isGstActiveStatus(gstDetails.status)
                                            ? 'badge-success'
                                            : 'badge-warning'
                                        }`}
                                      >
                                        {gstDetails.status || 'Unknown'}
                                      </span>
                                    </p>
                                    {gstDetails.legalName && (
                                      <p className="mb-1">
                                        <strong>Legal Name:</strong> {gstDetails.legalName}
                                      </p>
                                    )}
                                    {gstDetails.tradeName && (
                                      <p className="mb-1">
                                        <strong>Trade Name:</strong> {gstDetails.tradeName}
                                      </p>
                                    )}
                                    {gstDetails.constitutionType && (
                                      <p className="mb-1">
                                        <strong>Constitution Type:</strong>{' '}
                                        {gstDetails.constitutionType}
                                      </p>
                                    )}
                                    {gstDetails.taxpayerType && (
                                      <p className="mb-1">
                                        <strong>Taxpayer Type:</strong> {gstDetails.taxpayerType}
                                      </p>
                                    )}
                                    {gstDetails.state && (
                                      <p className="mb-1">
                                        <strong>State:</strong> {gstDetails.state}
                                      </p>
                                    )}
                                    {gstDetails.registrationDate && (
                                      <p className="mb-0">
                                        <strong>Registration Date:</strong>{' '}
                                        {gstDetails.registrationDate}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {gstError && (
                            <div
                              className="mt-2 p-2"
                              style={{
                                backgroundColor: '#f8d7da',
                                border: '1px solid #f5c6cb',
                                borderRadius: '4px'
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <i className="fa fa-exclamation-circle text-danger mr-2"></i>
                                <span style={{ fontSize: '12px', color: '#721c24' }}>{gstError}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="row mb-2 mt-3">
                        <div className="col-12">
                          <h5 className="text-primary border-bottom pb-2 mb-3">
                            <i className="fa-solid fa-address-book mr-2" />
                            Contacts
                          </h5>
                        </div>
                      </div>

                      {contacts.map((contact, idx) => (
                        <div
                          key={contact._id || `new-${idx}`}
                          className="border rounded p-3 mb-3"
                          style={{ backgroundColor: '#f8f9fa' }}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                            <h6 className="mb-0 text-primary fwt_600">
                              Contact {idx + 1}
                              {contact._id ? (
                                <small className="text-muted ml-2 font-weight-normal">
                                  (existing)
                                </small>
                              ) : (
                                <small className="text-muted ml-2 font-weight-normal">(new)</small>
                              )}
                            </h6>
                            {contacts.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeContact(idx)}
                              >
                                <i className="fa-solid fa-trash mr-1" />
                                Remove
                              </button>
                            )}
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Contact Person</label>
                            <div className="col-12 col-md-8">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Contact person"
                                value={contact.contactPerson}
                                onChange={(e) =>
                                  updateContact(idx, 'contactPerson', e.target.value)
                                }
                              />
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Designation</label>
                            <div className="col-12 col-md-8">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Designation (optional)"
                                value={contact.designation}
                                onChange={(e) =>
                                  updateContact(idx, 'designation', e.target.value)
                                }
                              />
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Email</label>
                            <div className="col-12 col-md-8">
                              <input
                                type="email"
                                className="form-control"
                                placeholder="Email"
                                value={contact.email}
                                onChange={(e) => updateContact(idx, 'email', e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Contact Number</label>
                            <div className="col-12 col-md-8">
                              <input
                                type="tel"
                                className="form-control"
                                placeholder="10-digit mobile"
                                value={contact.contactNumber}
                                onChange={(e) => handleContactPhoneChange(idx, e)}
                                onKeyPress={(e) => {
                                  if (
                                    !/[0-9]/.test(e.key) &&
                                    e.key !== 'Backspace' &&
                                    e.key !== 'Delete' &&
                                    e.key !== 'Tab'
                                  ) {
                                    e.preventDefault()
                                  }
                                }}
                                maxLength={10}
                                inputMode="numeric"
                              />
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Address</label>
                            <div className="col-12 col-md-8">
                              <textarea
                                className="form-control"
                                placeholder="Address"
                                rows={3}
                                value={contact.address}
                                onChange={(e) =>
                                  updateContact(idx, 'address', e.target.value)
                                }
                              />
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">City</label>
                            <div className="col-12 col-md-8">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="City"
                                value={contact.city}
                                onChange={(e) => handleContactCityChange(idx, e)}
                              />
                            </div>
                          </div>

                          <div className="form-group row mb-0">
                            <label className="col-12 col-md-4 col-form-label">State</label>
                            <div className="col-12 col-md-8">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="State"
                                value={contact.state}
                                onChange={(e) => handleContactStateChange(idx, e)}
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <div className="form-group row">
                        <div className="col-12 col-md-8 offset-md-4">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={addContact}
                          >
                            <i className="fa-solid fa-plus mr-1" />
                            Add another contact
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="card-footer text-center">
                      <button type="submit" className="btn btn-blue" disabled={isLoading}>
                        {isLoading ? 'Saving...' : 'Update'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerMasterEdit
